import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { fmtTL, fmtDate, fmtInt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  CheckCircle2, XCircle, FileSignature, ShieldCheck, MessageSquare,
  Calendar, Hash, FileText, Building2, Phone, Mail, Clock,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DECISION_LABELS = {
  approved: { label: "Onaylandı", color: "#16A34A", icon: CheckCircle2 },
  rejected: { label: "Reddedildi", color: "#DC2626", icon: XCircle },
  revision_requested: { label: "Revizyon Talep Edildi", color: "#F59E0B", icon: FileSignature },
};

export default function QuoteDecision() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null);
  const [revisionNote, setRevisionNote] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/public/quotes/${token}`)
      .then((r) => setQuote(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Teklif bulunamadı"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!decision) return;
    if (decision === "revision_requested" && !revisionNote.trim()) {
      toast.error("Lütfen revize istediğiniz konuları yazınız.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/quotes/${token}/decision`, {
        decision,
        revision_note: revisionNote,
        customer_name: customerName,
      });
      const { data } = await axios.get(`${API}/public/quotes/${token}`);
      setQuote(data);
      toast.success("Cevabınız iletildi. Teşekkür ederiz.");
      setDecision(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]">
        <div className="text-[#08285A] font-bold">Yükleniyor...</div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB] p-6">
        <div className="azd-card p-8 max-w-md text-center">
          <XCircle size={48} className="text-[#DC2626] mx-auto mb-3" />
          <h2 className="text-xl font-extrabold text-[#08285A] mb-2">Teklif Açılamadı</h2>
          <p className="text-sm text-[#6B7280]">{error}</p>
        </div>
      </div>
    );
  }

  const isDecided = quote.customer_decision && ["approved", "rejected"].includes(quote.customer_decision);
  const isRevision = quote.customer_decision === "revision_requested";

  return (
    <div className="min-h-screen bg-[#F6F8FB]">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="bg-[#08285A] text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-5">
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-xl px-5 py-3 shadow-lg">
              <img src="/azd-logo.png" alt="AZD Ambalaj"
                   className="h-12 w-auto" />
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#FFE600] font-bold">
              Müşteri Teklif<br/>Onay Portali
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 lg:px-8 py-6 space-y-5">
        {/* Status banner */}
        {(isDecided || isRevision) && (
          <div
            className="azd-card p-5 flex items-start gap-4 border-l-4"
            style={{ borderLeftColor: DECISION_LABELS[quote.customer_decision]?.color }}
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: DECISION_LABELS[quote.customer_decision].color + "20", color: DECISION_LABELS[quote.customer_decision].color }}
            >
              {(() => {
                const I = DECISION_LABELS[quote.customer_decision].icon;
                return <I size={22} />;
              })()}
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-[#6B7280] font-bold">
                Mevcut Durum
              </div>
              <div
                className="text-xl font-extrabold mt-0.5"
                style={{ color: DECISION_LABELS[quote.customer_decision].color }}
              >
                {DECISION_LABELS[quote.customer_decision].label}
              </div>
              <div className="text-xs text-[#6B7280] mt-1">
                {fmtDate(quote.customer_decision_at)} tarihinde kaydedildi
              </div>
              {quote.customer_revision_note && (
                <div className="mt-3 p-3 bg-[#FEF3C7]/40 border border-[#FCD34D] rounded-lg text-sm text-[#854D0E]">
                  <div className="font-bold text-xs uppercase tracking-wider mb-1">Revize Notu:</div>
                  {quote.customer_revision_note}
                </div>
              )}
              {isDecided && (
                <div className="text-sm text-[#6B7280] mt-2">
                  Karar verildikten sonra değişiklik için lütfen satış temsilcinizle iletişime geçiniz.
                </div>
              )}
            </div>
          </div>
        )}

        {quote.is_expired && !isDecided && (
          <div className="azd-card p-4 border-l-4 border-[#DC2626] bg-[#FEE2E2]/30">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-[#DC2626]" />
              <div className="text-sm font-bold text-[#991B1B]">
                Bu teklifin geçerlilik süresi dolmuştur. Lütfen yeni teklif için satış temsilcinizle iletişime geçiniz.
              </div>
            </div>
          </div>
        )}

        {/* Quote details */}
        <div className="azd-card p-6">
          <div className="flex items-start justify-between gap-4 mb-5 pb-5 border-b border-[#E5E7EB]">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#0B3676] font-bold mb-1">
                Teklif
              </div>
              <div className="text-3xl font-extrabold text-[#08285A] font-mono">{quote.quote_no}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Toplam</div>
              <div className="text-2xl font-extrabold text-[#0B3676]">{fmtTL(quote.total_amount)}</div>
              <div className="text-[10px] text-[#DC2626] font-semibold">KDV hariç</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <Info icon={Building2} label="Müşteri" value={quote.company_name} />
            <Info icon={FileText} label="İlgili Kişi" value={`${quote.contact_person} (${quote.contact_title})`} />
            <Info icon={Calendar} label="Teklif Tarihi" value={fmtDate(quote.quote_date)} />
            <Info icon={Clock} label="Geçerlilik" value={fmtDate(quote.valid_until)} />
            <Info icon={Phone} label="Telefon" value={quote.phone} />
            <Info icon={Mail} label="E-Posta" value={quote.email} />
            <Info icon={ShieldCheck} label="Teslimat Süresi" value={quote.delivery_time} />
            <Info icon={Hash} label="Vade" value={`${quote.payment_term_days} gün`} />
          </div>

          {quote.notes && (
            <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold mb-1">
                Açıklama
              </div>
              <div className="text-sm text-[#374151] whitespace-pre-line">{quote.notes}</div>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="azd-card overflow-hidden">
          <div className="px-5 py-3 bg-[#08285A] text-white">
            <div className="text-sm font-bold uppercase tracking-wider">Ürün Listesi</div>
          </div>
          <div className="overflow-x-auto azd-scrollbar">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold w-10">#</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Ürün</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Miktar</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Birim Fiyat</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {quote.items.map((it, i) => (
                  <tr key={i} className="border-t border-[#E5E7EB]">
                    <td className="px-4 py-3 text-xs text-[#6B7280]">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#111827]">{it.product_name}</div>
                      {it.product_code && (
                        <div className="text-xs text-[#6B7280] font-mono">{it.product_code}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {fmtInt(it.quantity)} <span className="text-[#6B7280] text-xs">{it.unit}</span>
                    </td>
                    <td className="px-4 py-3 text-right">{fmtTL(it.unit_price)}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#08285A]">{fmtTL(it.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#F6F8FB]">
                <tr>
                  <td colSpan={4} className="px-4 py-2 text-right text-sm text-[#6B7280]">Ara Toplam:</td>
                  <td className="px-4 py-2 text-right font-semibold">{fmtTL(quote.subtotal)}</td>
                </tr>
                {quote.discount_amount > 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-2 text-right text-sm text-[#6B7280]">İskonto:</td>
                    <td className="px-4 py-2 text-right">-{fmtTL(quote.discount_amount)}</td>
                  </tr>
                )}
                <tr className="bg-[#08285A] text-white">
                  <td colSpan={4} className="px-4 py-3 text-right font-bold uppercase tracking-wider text-sm">
                    Genel Toplam:
                  </td>
                  <td className="px-4 py-3 text-right font-extrabold text-base">{fmtTL(quote.total_amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="px-5 py-2 bg-[#FEF3C7]/30 text-right text-[11px] font-bold text-[#854D0E]">
            Fiyatlarımıza KDV dahil değildir.
          </div>
        </div>

        {/* Decision area */}
        {!quote.customer_decision && !quote.is_expired && (
          <div className="azd-card p-6">
            <div className="text-base font-extrabold text-[#08285A] mb-1">
              Cevabınızı Bekliyoruz
            </div>
            <p className="text-sm text-[#6B7280] mb-5">
              Aşağıdaki üç seçenekten birini seçerek bize hızlıca dönüş yapabilirsiniz.
            </p>

            <div className="grid md:grid-cols-3 gap-3 mb-5">
              <button
                type="button"
                data-testid="decision-approve"
                onClick={() => setDecision("approved")}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  decision === "approved"
                    ? "border-[#16A34A] bg-[#16A34A]/5 shadow-md"
                    : "border-[#E5E7EB] hover:border-[#16A34A]/50 bg-white"
                }`}
              >
                <CheckCircle2 size={22} className="text-[#16A34A] mb-2" />
                <div className="font-extrabold text-[#16A34A]">Onayla</div>
                <div className="text-xs text-[#6B7280] mt-1">
                  Bu teklifi olduğu gibi kabul ediyorum.
                </div>
              </button>

              <button
                type="button"
                data-testid="decision-revision"
                onClick={() => setDecision("revision_requested")}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  decision === "revision_requested"
                    ? "border-[#F59E0B] bg-[#F59E0B]/5 shadow-md"
                    : "border-[#E5E7EB] hover:border-[#F59E0B]/50 bg-white"
                }`}
              >
                <MessageSquare size={22} className="text-[#F59E0B] mb-2" />
                <div className="font-extrabold text-[#F59E0B]">Revize İste</div>
                <div className="text-xs text-[#6B7280] mt-1">
                  Bazı kalemlerin gözden geçirilmesini istiyorum.
                </div>
              </button>

              <button
                type="button"
                data-testid="decision-reject"
                onClick={() => setDecision("rejected")}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  decision === "rejected"
                    ? "border-[#DC2626] bg-[#DC2626]/5 shadow-md"
                    : "border-[#E5E7EB] hover:border-[#DC2626]/50 bg-white"
                }`}
              >
                <XCircle size={22} className="text-[#DC2626] mb-2" />
                <div className="font-extrabold text-[#DC2626]">Reddet</div>
                <div className="text-xs text-[#6B7280] mt-1">
                  Bu teklifi şu an için kabul etmiyorum.
                </div>
              </button>
            </div>

            {decision === "revision_requested" && (
              <div className="space-y-3 mb-4 azd-fade-up">
                <div>
                  <label className="block text-xs font-bold text-[#111827] mb-1.5 uppercase tracking-wider">
                    Hangi konuları revize etmek istiyorsunuz? <span className="text-[#DC2626]">*</span>
                  </label>
                  <Textarea
                    data-testid="revision-note"
                    value={revisionNote}
                    onChange={(e) => setRevisionNote(e.target.value)}
                    placeholder="Örneğin: 2. kalemin fiyatını gözden geçirmenizi rica ederim. 3. kaleme alternatif bir ürün önerisi alabilir miyim?"
                    rows={5}
                    className="resize-none"
                  />
                  <div className="text-[11px] text-[#6B7280] mt-1">
                    Notunuz satış ekibimize iletilecek ve revize edilmiş yeni teklif hazırlanacaktır.
                  </div>
                </div>
              </div>
            )}

            {decision && (
              <div className="space-y-3 mb-4 azd-fade-up">
                <div>
                  <label className="block text-xs font-bold text-[#111827] mb-1.5 uppercase tracking-wider">
                    Adınız Soyadınız (opsiyonel)
                  </label>
                  <Input
                    data-testid="customer-name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Cevabı kaydederken bizim için yardımcı olur"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-end items-center pt-3 border-t border-[#E5E7EB]">
              {decision && (
                <button
                  type="button"
                  onClick={() => { setDecision(null); setRevisionNote(""); }}
                  className="text-sm text-[#6B7280] font-semibold hover:text-[#0B3676]"
                >
                  Seçimi Temizle
                </button>
              )}
              <Button
                type="button"
                disabled={!decision || submitting}
                onClick={submit}
                data-testid="submit-decision"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white px-6 h-11 font-semibold disabled:opacity-50"
              >
                {submitting ? "Gönderiliyor..." : "Cevabımı Gönder"}
              </Button>
            </div>
          </div>
        )}

        <div className="text-center text-xs text-[#6B7280] py-6">
          AZD Ambalaj San. ve Tic. Ltd. Şti. • Hazırlayan: <b>{quote.prepared_by_name}</b>
        </div>
      </main>
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <div className="w-8 h-8 rounded-lg bg-[#F6F8FB] flex items-center justify-center shrink-0 text-[#0B3676]">
      <Icon size={14} />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">{label}</div>
      <div className="text-sm font-semibold text-[#111827] truncate">{value || "-"}</div>
    </div>
  </div>
);
